import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import config from "../../config";
import { useLanguage } from "../../Context";
import { MdAudiotrack } from "react-icons/md";

export default function Audio() {
  const [contents, setContents] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isLatestLoading, setIsLatestLoading] = useState(true);
  const [latestContent, setLatestContent] = useState([]);
  const [latestId, setLatestId] = useState(null);

  const { language } = useLanguage();

  // Fetch latest audio and set latestId
  useEffect(() => {
    const fetchLatestAudio = async () => {
      const response = await fetch(
        `${config.apiUrl}contentapi.php?option=1&type=7&categoryid=2&subcategory=0&limitCount=1&lang=${language}`
      );
      const latestAudio = await response.json();
      setLatestContent(latestAudio);
      setLatestId(latestAudio[0]?.id || null);
      setIsLatestLoading(false);
    };
    fetchLatestAudio();
    setPage(1);
    setContents([]);
    setHasMore(true);
  }, [language]);

  // Fetch data when latestId or page changes
  useEffect(() => {
    if (latestId === null) return;
    const fetchData = async () => {
      if (!hasMore) return;

      setIsLoading(true);

      try {
        const response = await fetch(
          `${config.apiUrl}lazyLoad.php?option=2&categoryid=2&subcategory=0&page=${page}&lang=${language}&id=${latestId}`
        );
        const data = await response.json();
        const newContents = data.map((item) => ({
          id: item.id,
          categoryE: item.categoryE,
          FileUrl: item.FileUrl,
          ImageUrl: item.ImageUrl,
          PostDate: item.PostDate,
          ShortDesc: item.ShortDesc,
          SubCategory: item.SubCategory,
          SubCategoryd: item.SubCategoryd,
          Title: item.Title,
          YoutubeUrl: item.YoutubeUrl,
          contentViews: item.contentViews,
          fulldesc: item.fulldesc,
        }));
        setContents((prev) => [...prev, ...newContents]);
        setHasMore(newContents.length > 0);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line
  }, [latestId, page, language]);

  const handleScroll = () => {
    const scrollOffset = 200;
    const isBottom =
      window.innerHeight + window.scrollY + scrollOffset >=
      document.documentElement.scrollHeight;
    if (isBottom && !isLoading && hasMore) {
      setPage((prev) => prev + 1);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line
  }, [isLoading, hasMore]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [page]);

  ////// Markup //////
  const styles = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(20rem, 2fr))",
    gap: "1em",
  };

  const audios = contents.map((audio) => (
    <div key={audio.id} className="card bg-body-secondary border-0 rounded-1">
      <Link
        to={`/audio/${audio.SubCategory}/${audio.id}`}
        className="icon-links blue-links text-body"
      >
        <div className="ratio ratio-16x9">
          <MdAudiotrack
            className="position-absolute m-2"
            alt=""
            style={{
              width: "2.5rem",
              height: "2.5rem",
              color: "white",
              border: "0.2rem solid white",
              borderRadius: "50%",
              padding: "0.3rem",
              zIndex: "10",
              right: "0",
            }}
          />
          <img
            className="w-100 h-100 object-fit-cover"
            src={audio.ImageUrl?.replace(
              /_large(?=\.(jpg|jpeg|png)$)/,
              "_thumb"
            )}
            alt="audio thumbnails"
          />
        </div>
        <p className="fs-6 m-0 text-center p-3">{audio.Title}</p>
      </Link>
    </div>
  ));

  const latestAudio = latestContent.map((audio) => (
    <div
      key={audio.id}
      className="d-flex flex-column flex-lg-row bg-body-secondary mt-4 mb-4"
    >
      <Link
        to={`/audio/${audio.SubCategory}/${audio.id}`}
        className="col-lg-6 latest-heigth"
        style={{ position: "relative" }}
      >
        <MdAudiotrack
          className="icon-links"
          style={{
            width: "6.5rem",
            height: "6.5rem",
            position: "absolute",
            left: "0",
            right: "0",
            top: "0",
            bottom: "0",
            margin: "auto",
            color: "#ffae00",
            border: "0.4rem solid #ffae00",
            borderRadius: "50%",
            padding: "1rem",
          }}
        />
        <img
          className="w-100 h-100 object-fit-cover"
          src={audio.ImageUrl}
          alt=""
          style={{ maxHeight: "25em" }}
        />
      </Link>
      <div className="col-lg-6 p-3 secondary-bg-yellow">
        <h2 className="border-end border-5 pe-2 text-light" id="border-light">
          {audio.Title}
        </h2>
        <p>{audio.ShortDesc?.slice(0, 500)}</p>
      </div>
    </div>
  ));

  return (
    <div className="bg-body">
      <div className="max-container-width py-3 text-body">
        {isLatestLoading ? (
          <div className="d-flex flex-column flex-lg-row bg-body-secondary mt-4 mb-4">
            <div
              className="col-lg-6 bg-body-tertiary"
              style={{ minHeight: "38vh" }}
            />
            <div
              className="col-lg-6 p-3"
              style={{ backgroundColor: "#ffae00" }}
            >
              <div className="placeholder-glow">
                <span
                  className="placeholder col-12 mb-3 text-white"
                  style={{ height: "1.8rem" }}
                />
                <span
                  className="placeholder col-6 mb-3 text-white"
                  style={{ height: "1.8rem" }}
                />
                <span className="placeholder col-12 mb-3" />
                <span className="placeholder col-12 mb-3" />
                <span className="placeholder col-12 mb-3" />
                <span className="placeholder col-8 mb-3" />
              </div>
            </div>
          </div>
        ) : (
          latestAudio
        )}
        <div style={styles} className="mb-4">
          {audios}
        </div>
        {isLoading && (
          <div className="placeholder-glow mb-4" style={styles}>
            {[...Array(5)].map((_, index) => (
              <div key={index} className="bg-body-secondary">
                <div className="w-100 placeholder" style={{ height: "7rem" }} />
                <div className="p-3">
                  <div className="col-12 placeholder" />
                  <div className="col-6 placeholder" />
                </div>
              </div>
            ))}
          </div>
        )}
        {!hasMore && (
          <div className="mt-5 text-center">
            <small className="p-2 bg-secondary text-light rounded">
              {parseInt(language) === 1 ? "پایان مطالب" : "د محتوا پای"}
            </small>
          </div>
        )}
      </div>
    </div>
  );
}
